/**
 * Authentication Context Provider using Supabase
 * Manages user authentication state with Supabase Auth
 * Includes credit balance for credit-based billing
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { User, Session, AuthError, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export interface BillingStatus {
    has_subscription: boolean;
    status: string | null;
    plan: 'free' | 'pro' | string;
    page_limit: number;
    credits: number;
    has_metered_billing: boolean;
    current_period_end?: string | null;
    stripe_customer_id?: string | null;
}

interface AuthContextType {
    user: User | null;
    session: Session | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    billing: BillingStatus | null;
    isBillingLoading: boolean;
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (email: string, password: string, fullName?: string) => Promise<void>;
    signInWithGoogle: (redirectTo?: string) => Promise<void>;
    signOut: () => Promise<void>;
    clearError: () => void;
    refreshBilling: () => Promise<void>;
    /** @deprecated Use billing instead */
    subscription: BillingStatus | null;
    /** @deprecated Use isBillingLoading instead */
    isSubscriptionLoading: boolean;
    /** @deprecated Use refreshBilling instead */
    refreshSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const BILLING_CACHE_KEY = 'quickmask_billing_cache';

const DEFAULT_BILLING: BillingStatus = {
    has_subscription: false,
    status: null,
    plan: 'free',
    page_limit: 6,
    credits: 0,
    has_metered_billing: false,
};

const getCachedBilling = (userId: string): BillingStatus | null => {
    try {
        const cached = localStorage.getItem(BILLING_CACHE_KEY);
        if (!cached) return null;
        const data = JSON.parse(cached);
        if (data.userId === userId && Date.now() - data.timestamp < 30000) {
            return data.billing;
        }
        return null;
    } catch {
        return null;
    }
};

const setCachedBilling = (userId: string, billing: BillingStatus): void => {
    try {
        localStorage.setItem(BILLING_CACHE_KEY, JSON.stringify({
            userId,
            billing,
            timestamp: Date.now(),
        }));
    } catch {
        // Ignore localStorage errors
    }
};

const clearCachedBilling = (): void => {
    try {
        localStorage.removeItem(BILLING_CACHE_KEY);
        localStorage.removeItem('quickmask_subscription_cache');
    } catch {
        // Ignore localStorage errors
    }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [billing, setBilling] = useState<BillingStatus | null>(null);
    const [isBillingLoading, setIsBillingLoading] = useState(false);
    const fetchedForUserRef = useRef<string | null>(null);

    const isAuthenticated = !!user;

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const handleAuthError = (error: AuthError) => {
        console.error('Auth error:', error);
        setError(error.message);
    };

    const fetchBillingFromBackend = useCallback(async (accessToken: string): Promise<BillingStatus> => {
        try {
            const response = await fetch('/api/stripe/subscription-status', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok) return DEFAULT_BILLING;
            const data = await response.json();
            return data as BillingStatus;
        } catch {
            return DEFAULT_BILLING;
        }
    }, []);

    const fetchBillingStatus = useCallback(async (userId: string, accessToken: string): Promise<BillingStatus> => {
        const result = await fetchBillingFromBackend(accessToken);
        setCachedBilling(userId, result);
        return result;
    }, [fetchBillingFromBackend]);

    const refreshBilling = useCallback(async () => {
        if (!session?.access_token || !user?.id) {
            setBilling(DEFAULT_BILLING);
            return;
        }
        setIsBillingLoading(true);
        try {
            const status = await fetchBillingStatus(user.id, session.access_token);
            setBilling(status);
        } finally {
            setIsBillingLoading(false);
        }
    }, [session?.access_token, user?.id, fetchBillingStatus]);

    useEffect(() => {
        let mounted = true;

        const initializeAuth = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!mounted) return;

            setSession(session);
            setUser(session?.user ?? null);

            if (session?.user) {
                const cached = getCachedBilling(session.user.id);
                if (cached) setBilling(cached);

                fetchBillingStatus(session.user.id, session.access_token).then((status) => {
                    if (mounted) {
                        setBilling(status);
                        fetchedForUserRef.current = session.user.id;
                    }
                });
            }

            setIsLoading(false);
        };

        initializeAuth();

        const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
            async (_event: AuthChangeEvent, session: Session | null) => {
                if (!mounted) return;

                setSession(session);
                setUser(session?.user ?? null);

                if (session?.user) {
                    if (fetchedForUserRef.current !== session.user.id) {
                        const cached = getCachedBilling(session.user.id);
                        if (cached) setBilling(cached);

                        fetchBillingStatus(session.user.id, session.access_token).then((status) => {
                            if (mounted) {
                                setBilling(status);
                                fetchedForUserRef.current = session.user.id;
                            }
                        });
                    }
                } else {
                    setBilling(null);
                    clearCachedBilling();
                    fetchedForUserRef.current = null;
                }

                setIsLoading(false);
            }
        );

        return () => {
            mounted = false;
            authSubscription.unsubscribe();
        };
    }, [fetchBillingStatus]);

    const signIn = useCallback(async (email: string, password: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const { error } = await supabase.auth.signInWithPassword({ email, password });
            if (error) { handleAuthError(error); throw error; }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const signUp = useCallback(async (email: string, password: string, fullName?: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const { error } = await supabase.auth.signUp({
                email, password,
                options: { data: { full_name: fullName } },
            });
            if (error) { handleAuthError(error); throw error; }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const signInWithGoogle = useCallback(async (redirectTo?: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    // Root origin loads the marketing landing page; send users into the app after OAuth.
                    redirectTo: redirectTo ?? `${window.location.origin}/app`,
                },
            });
            if (error) { handleAuthError(error); throw error; }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const signOut = useCallback(async () => {
        setIsLoading(true);
        try {
            const { error } = await supabase.auth.signOut();
            if (error) { handleAuthError(error); throw error; }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const value: AuthContextType = {
        user,
        session,
        isAuthenticated,
        isLoading,
        error,
        billing,
        isBillingLoading,
        signIn,
        signUp,
        signInWithGoogle,
        signOut,
        clearError,
        refreshBilling,
        // Backward-compat aliases
        subscription: billing,
        isSubscriptionLoading: isBillingLoading,
        refreshSubscription: refreshBilling,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const useAccessToken = () => {
    const { session } = useAuth();
    return session?.access_token || null;
};
